import posthog from "posthog-js";
import { getActivePinia } from "pinia";
import type { App as VueApp } from "vue";
import { useAuthStore } from "@/stores/auth";

let posthogReady = false;

/** Один раз на экземпляр Error: иначе дублируются `throw` из API и `unhandledrejection`. */
const posthogReportedErrors = new WeakSet<object>();

/** Уже отправлено в PostHog (например из `geniFetch`) — можно подавить `unhandledrejection`. */
export function wasAppErrorReportedToPosthog(error: unknown): boolean {
  return error instanceof Error && posthogReportedErrors.has(error);
}

export type AppErrorChannel = "vue" | "unhandled_rejection" | "window_error" | "toast" | "geni_backend";

/**
 * Отправляет ошибку в PostHog (Issues через `captureException` или событие `[CardGenius] Ошибка`).
 * Без `VITE_POSTHOG_KEY` — no-op.
 */
export function reportAppError(args: {
  channel: AppErrorChannel;
  error?: unknown;
  /** Подпись, если нет нормального Error (например тост только строками). */
  message?: string;
  extra?: Record<string, unknown>;
}): void {
  if (!posthogKey() || typeof window === "undefined") return;
  if (!posthogReady) initPosthog();
  if (!posthogReady) return;

  const user = analyticsUserSnapshot();
  const extra = args.extra ?? {};
  const err = args.error;

  if (err instanceof Error) {
    if (posthogReportedErrors.has(err)) return;
    posthogReportedErrors.add(err);
    const props = errorExceptionProps(err, extra, args.message);
    // Hint PostHog Issues grouping: $exception_type/$exception_message override
    // the SDK's defaults (err.constructor.name / err.message). Without this, all
    // structured backend errors would group as "Error" and lose visibility.
    const overrides: Record<string, string> = {
      $exception_type: props.error_type,
      $exception_message: props.error_message,
    };
    posthog.captureException(err, {
      ...user,
      ...props,
      ...overrides,
      error_channel: args.channel,
      ...(args.message ? { report_message: args.message } : {}),
      ...extra,
    });
    return;
  }

  const msg =
    args.message ??
    (typeof err === "string"
      ? err
      : err != null && typeof (err as { message?: unknown }).message === "string"
        ? String((err as { message: string }).message)
        : "Unknown error");

  posthog.capture("[CardGenius] Ошибка", {
    ...user,
    error_channel: args.channel,
    message: msg,
    ...(err != null && typeof err !== "string" ? { raw_reason: String(err) } : {}),
    ...extra,
  });
}

function truncateForAnalytics(value: string, maxLen: number): string {
  if (value.length <= maxLen) return value;
  return `${value.slice(0, maxLen)}…`;
}

const ERROR_STACK_MAX_LEN = 8000;
const ERROR_MESSAGE_MAX_LEN = 2000;

/**
 * Текст для `error_message` в $exception: не оставляем пустую строку (иначе notEmpty в PostHog = 0%).
 * Приоритет: report_message → geni_api_message (extra) → apiMessage на Error → err.message → HTTP status.
 */
function resolveErrorMessageForPosthog(
  err: Error,
  extra: Record<string, unknown>,
  reportMessage?: string,
): string {
  const fromReport = reportMessage?.trim();
  if (fromReport) return truncateForAnalytics(fromReport, ERROR_MESSAGE_MAX_LEN);

  const fromExtraApi =
    typeof extra.geni_api_message === "string" ? extra.geni_api_message.trim() : "";
  if (fromExtraApi) return truncateForAnalytics(fromExtraApi, 500);

  const apiMessage = (err as { apiMessage?: string }).apiMessage?.trim();
  if (apiMessage) return truncateForAnalytics(apiMessage, 500);

  const fromMessage = err.message?.trim();
  if (fromMessage) return truncateForAnalytics(fromMessage, ERROR_MESSAGE_MAX_LEN);

  if (typeof extra.http_status === "number") {
    return `HTTP ${extra.http_status}`;
  }

  return "Unknown error";
}

/**
 * Тип ошибки для группировки в PostHog Issues:
 * - geni_error_code из backend (AI_PROVIDER_TIMEOUT, …) — высший приоритет;
 *   это и есть «стабильный fingerprint» для ошибок API.
 * - HTTP_<status> — следующий по точности для бэкенд-ошибок без error_code.
 * - constructor.name — для обычных runtime-ошибок (TypeError, ...).
 *
 * PostHog индексирует именно $exception_type, поэтому если оставить просто
 * "Error" — все ошибки сольются в одну группу.
 */
function resolveErrorTypeForPosthog(
  err: Error,
  extra: Record<string, unknown>,
): string {
  const code =
    typeof extra.geni_error_code === "string" ? extra.geni_error_code.trim() : "";
  if (code) return code;

  const errOwnCode = (err as { errorCode?: string }).errorCode?.trim();
  if (errOwnCode) return errOwnCode;

  const status =
    typeof extra.http_status === "number"
      ? extra.http_status
      : (err as { status?: number }).status;
  if (typeof status === "number" && status > 0) {
    return `HTTP_${status}`;
  }

  return err.constructor?.name || "Error";
}

/** Плоские поля для дашбордов PostHog ($exception); SDK кладёт stack в `$exception_list`. */
function errorExceptionProps(
  err: Error,
  extra: Record<string, unknown>,
  reportMessage?: string,
): Record<string, string> {
  const props: Record<string, string> = {
    error_message: resolveErrorMessageForPosthog(err, extra, reportMessage),
    error_type: resolveErrorTypeForPosthog(err, extra),
    error_name: err.name || "Error",
  };
  if (err.stack) {
    props.error_stack = truncateForAnalytics(err.stack, ERROR_STACK_MAX_LEN);
  }
  // Pull structured-error metadata onto top-level $exception props so they're
  // searchable / filterable in PostHog without digging into `extra`.
  const requestId =
    typeof extra.geni_request_id === "string" ? extra.geni_request_id : undefined;
  if (requestId) {
    props.request_id = requestId;
  }
  const errorCode = props.error_type;
  if (errorCode && errorCode !== (err.constructor?.name || "Error")) {
    props.error_code = errorCode;
  }
  return props;
}

/** Тосты с ошибкой: отдельное событие с текстом для дашбордов. */
export function reportToastErrorToPosthog(item: { title: string; description?: string; id: string }): void {
  if (!posthogKey() || typeof window === "undefined") return;
  if (!posthogReady) initPosthog();
  if (!posthogReady) return;
  const user = analyticsUserSnapshot();
  posthog.capture("[CardGenius] Ошибка (тост)", {
    ...user,
    error_channel: "toast",
    toast_id: item.id,
    toast_title: truncateForAnalytics(item.title, 500),
    ...(item.description ? { toast_description: truncateForAnalytics(item.description, 2000) } : {}),
  });
}

const WINDOW_ERROR_LISTENERS_KEY = "__cg_posthog_error_listeners__";

const VUE_ERROR_BURST_WINDOW_MS = 5000;
const VUE_ERROR_BURST_MAX = 5;
const vueErrorBurstCounts = new Map<string, { count: number; windowStart: number }>();

function vueErrorFingerprint(err: unknown, info: string, componentName?: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  return `${componentName ?? ""}|${info}|${msg}`;
}

function shouldReportVueError(err: unknown, info: string, componentName?: string): boolean {
  const key = vueErrorFingerprint(err, info, componentName);
  const now = Date.now();
  const entry = vueErrorBurstCounts.get(key);
  if (!entry || now - entry.windowStart > VUE_ERROR_BURST_WINDOW_MS) {
    vueErrorBurstCounts.set(key, { count: 1, windowStart: now });
    return true;
  }
  entry.count += 1;
  return entry.count <= VUE_ERROR_BURST_MAX;
}

/** Глобальные ошибки и необработанные промисы + цепочка Vue `errorHandler`. */
export function installGlobalErrorReporting(app: VueApp): void {
  if (typeof window === "undefined") return;

  const prev = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    const componentName = instance?.$options?.name ?? undefined;
    if (shouldReportVueError(err, info, componentName)) {
      reportAppError({
        channel: "vue",
        error: err,
        extra: { vue_error_info: info, component_name: componentName },
      });
    }
    if (typeof prev === "function") {
      prev(err, instance, info);
    } else if (import.meta.env.DEV) {
      console.error(err);
    }
  };

  const w = window as Window & Record<string, boolean | undefined>;
  if (w[WINDOW_ERROR_LISTENERS_KEY]) return;
  w[WINDOW_ERROR_LISTENERS_KEY] = true;

  window.addEventListener("error", (event) => {
    if (event.error instanceof Error) {
      reportAppError({
        channel: "window_error",
        error: event.error,
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    } else {
      reportAppError({
        channel: "window_error",
        message: event.message || "window error",
        error: event.error,
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (wasAppErrorReportedToPosthog(event.reason)) {
      event.preventDefault();
      return;
    }
    reportAppError({
      channel: "unhandled_rejection",
      error: event.reason,
      extra: {},
    });
  });
}

/** Первый заход: UTM из URL или cookie; после записи не перезаписывается (важно для OAuth-редиректов без query). */
const INITIAL_UTM_STORAGE_KEY = "cg_initial_touch_utm_v1";

const UTM_QUERY_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
type UtmQueryKey = (typeof UTM_QUERY_KEYS)[number];

const UTM_TO_INITIAL: Record<UtmQueryKey, string> = {
  utm_source: "initial_utm_source",
  utm_medium: "initial_utm_medium",
  utm_campaign: "initial_utm_campaign",
  utm_content: "initial_utm_content",
  utm_term: "initial_utm_term",
};

type InitialTouchPayload = {
  v: 1;
  initial_utm_source?: string;
  initial_utm_medium?: string;
  initial_utm_campaign?: string;
  initial_utm_content?: string;
  initial_utm_term?: string;
};

function trimUtmValue(raw: string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  const t = raw.trim();
  if (!t) return undefined;
  return t.length > 500 ? t.slice(0, 500) : t;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  if (parts.length < 2) return null;
  const value = parts.pop()?.split(";").shift();
  if (value == null) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function collectUtmFromUrl(): Partial<Omit<InitialTouchPayload, "v">> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out: Partial<Omit<InitialTouchPayload, "v">> = {};
  for (const q of UTM_QUERY_KEYS) {
    const val = trimUtmValue(params.get(q));
    if (val !== undefined) {
      out[UTM_TO_INITIAL[q] as keyof Omit<InitialTouchPayload, "v">] = val;
    }
  }
  return out;
}

function collectUtmFromCookies(): Partial<Omit<InitialTouchPayload, "v">> {
  const out: Partial<Omit<InitialTouchPayload, "v">> = {};
  for (const q of UTM_QUERY_KEYS) {
    const val = trimUtmValue(getCookie(q));
    if (val !== undefined) {
      out[UTM_TO_INITIAL[q] as keyof Omit<InitialTouchPayload, "v">] = val;
    }
  }
  return out;
}

/** Снимок first-touch: сначала query, затем добор из cookie для пустых полей. */
function mergeUrlThenCookieUtm(): Partial<Omit<InitialTouchPayload, "v">> {
  const fromUrl = collectUtmFromUrl();
  const fromCookie = collectUtmFromCookies();
  const keys: (keyof Omit<InitialTouchPayload, "v">)[] = [
    "initial_utm_source",
    "initial_utm_medium",
    "initial_utm_campaign",
    "initial_utm_content",
    "initial_utm_term",
  ];
  const merged: Partial<Omit<InitialTouchPayload, "v">> = {};
  for (const k of keys) {
    const u = fromUrl[k];
    if (u !== undefined) merged[k] = u;
    else if (fromCookie[k] !== undefined) merged[k] = fromCookie[k];
  }
  return merged;
}

function readStoredInitialTouch(): InitialTouchPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(INITIAL_UTM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InitialTouchPayload;
    if (parsed?.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Вызывать при старте приложения (до OAuth). Фиксирует UTM с первой страницы или из cookie один раз на браузер.
 */
export function ensureInitialUtmCaptured(): void {
  if (typeof window === "undefined") return;
  if (readStoredInitialTouch()) return;
  const merged = mergeUrlThenCookieUtm();
  const payload: InitialTouchPayload = { v: 1, ...merged };
  try {
    window.localStorage.setItem(INITIAL_UTM_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

/** Свойства для третьего аргумента posthog.identify (set once). */
function getInitialUtmSetOnceForIdentify(): Record<string, string> {
  const stored = readStoredInitialTouch();
  if (!stored) return {};
  const out: Record<string, string> = {};
  const keys = [
    "initial_utm_source",
    "initial_utm_medium",
    "initial_utm_campaign",
    "initial_utm_content",
    "initial_utm_term",
  ] as const;
  for (const k of keys) {
    const v = stored[k];
    if (typeof v === "string" && v.trim()) out[k] = v.trim();
  }
  return out;
}

/**
 * Временный киллсвитч PostHog: сервис ещё не запущен, а PostHog жрёт лимиты событий.
 * Модуль везде трактует пустой ключ как «аналитика выключена», поэтому обнуление ключа
 * здесь делает no-op все пути (initPosthog / captureEvent / reportAppError / identify).
 * Вернуть аналитику: POSTHOG_DISABLED = false.
 */
const POSTHOG_DISABLED: boolean = true;

function posthogKey(): string {
  if (POSTHOG_DISABLED) return "";
  return import.meta.env.VITE_POSTHOG_KEY?.trim() ?? "";
}

export function initPosthog(): void {
  if (posthogReady || typeof window === "undefined") return;
  ensureInitialUtmCaptured();
  const key = posthogKey();
  if (!key) return;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST?.trim() || "https://eu.i.posthog.com";
  posthog.init(key, {
    api_host: apiHost,
    capture_pageview: false,
    persistence: "localStorage+cookie",
  });
  posthogReady = true;
}

export function analyticsUserSnapshot(): {
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  user_phone: string | null;
} {
  const empty = {
    user_id: null as string | null,
    user_name: null as string | null,
    user_email: null as string | null,
    user_phone: null as string | null,
  };
  const pinia = getActivePinia();
  if (!pinia) return empty;
  const s = useAuthStore(pinia);
  const uid = s.cardGeniusAccountProfile?.userId?.trim();
  const name = s.accountName?.trim();
  const email = s.accountEmail?.trim();
  const phone = s.accountPhone?.trim();
  return {
    user_id: uid || null,
    user_name: name || null,
    user_email: email || null,
    user_phone: phone || null,
  };
}

export type RegistrationChannel = "phone" | "google" | "apple";

/** Успешное создание аккаунта (для дашбордов: count unique users по person_id). */
export function trackRegistrationCompleted(properties: {
  channel: RegistrationChannel;
  intent?: "register" | "login";
}): void {
  captureEvent("[CardGenius] Регистрация завершена", {
    channel: properties.channel,
    ...(properties.intent ? { intent: properties.intent } : {}),
  });
}

/** Все события дополняются снимком пользователя из Pinia (если Pinia уже смонтирована). */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!posthogKey()) return;
  if (typeof window === "undefined") return;
  if (!posthogReady) initPosthog();
  if (!posthogReady) return;
  const user = analyticsUserSnapshot();
  posthog.capture(event, { ...user, ...(properties ?? {}) });
}

export function identifyPosthogFromAuth(): void {
  if (!posthogKey()) return;
  if (!posthogReady) initPosthog();
  if (!posthogReady) return;
  ensureInitialUtmCaptured();
  const pinia = getActivePinia();
  if (!pinia) return;
  const s = useAuthStore(pinia);
  const uid = s.cardGeniusAccountProfile?.userId?.trim();
  if (!uid) return;
  const setOnce = getInitialUtmSetOnceForIdentify();
  posthog.identify(
    uid,
    {
      email: s.accountEmail?.trim() || undefined,
      phone: s.accountPhone?.trim() || undefined,
      name: s.accountName?.trim() || undefined,
    },
    setOnce
  );
}

export function resetPosthog(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(INITIAL_UTM_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  if (!posthogKey()) return;
  if (!posthogReady) return;
  posthog.reset();
}
